import emojiRegex from 'emoji-regex';
import { Channel } from '../../type';

const VideoTypes = [
  'video/quicktime',
  'video/mp4',
  'video/avi',
  'video/x-msvideo',
];

const ImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

const DocumentTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const supportedMediaTypes = [...VideoTypes, ...ImageTypes, ...DocumentTypes];
export const supportedMediaTypesString = supportedMediaTypes.join(',');

const isDocFile = (file: File) => DocumentTypes.includes(file.type);
const isVideoFile = (file: File) => VideoTypes.includes(file.type);
const isImageFile = (file: File) => ImageTypes.includes(file.type);

const MAX_VIDEO_DURATION = 10 * 60 + 0.5; // seconds
const MAX_MEDIA_SIZE = 400 * 1024 * 1024; // bytes
const MAX_DOC_FILE_SIZE = 25 * 1024 * 1024; // bytes

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise<number>((resolve) => {
    const video = document.createElement('video') as HTMLVideoElement;
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      // browser does not support this video format
      URL.revokeObjectURL(video.src);
      resolve(1);
    };
  });
};

const hasEmoji = (text: string | null): boolean =>
  !!emojiRegex().exec(text ?? '');

export type InputError = {
  messageId: string;
  files: File[];
};

export const validateMessageInput = async (
  text: string | null,
  files: File[],
): Promise<InputError | null> => {
  if (hasEmoji(text)) {
    return { messageId: 'message.input.error.has-emoji', files: [] };
  }

  if (files.length > 10) {
    return { messageId: 'message.input.error.too-many-files', files: [] };
  }

  const unsupportedFiles = files.filter(
    (file) => !supportedMediaTypes.includes(file.type),
  );
  if (unsupportedFiles.length) {
    return {
      messageId: 'message.input.error.unsupported-file-type',
      files: unsupportedFiles,
    };
  }

  const largeDocFiles = files.filter(
    (file) => isDocFile(file) && file.size > MAX_DOC_FILE_SIZE,
  );
  if (largeDocFiles.length) {
    return {
      messageId: 'message.input.error.file-too-large',
      files: largeDocFiles,
    };
  }

  const largeVideos = files.filter(
    (file) => isVideoFile(file) && file.size > MAX_MEDIA_SIZE,
  );
  if (largeVideos.length) {
    return {
      messageId: 'message.input.error.video-too-large',
      files: largeVideos,
    };
  }

  const largeImages = files.filter(
    (file) => isImageFile(file) && file.size > MAX_MEDIA_SIZE,
  );
  if (largeImages.length) {
    return {
      messageId: 'message.input.error.image-too-large',
      files: largeImages,
    };
  }

  const videoDurations = await Promise.all(
    files
      .filter((file) => isVideoFile(file))
      .map(async (file) => {
        const duration = await getVideoDuration(file);
        return { file, duration };
      }),
  );
  const tooLongVideos = videoDurations
    .filter((videoDuration) => videoDuration.duration > MAX_VIDEO_DURATION)
    .map((videoDuration) => videoDuration.file);
  if (tooLongVideos.length) {
    return {
      messageId: 'message.input.error.video-too-long',
      files: tooLongVideos,
    };
  }
  return null;
};

export const isFileUploadEnabledForChannel = (channel: Channel) =>
  channel.type !== 'SCHEDULING_TEAM' && channel.type !== 'PEER_TO_PEER';
